import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MapPin, Tag, BookmarkPlus, Check, ArrowRight } from "lucide-react";

export function discountLabel(o) {
  if (o.discount_type === "percentage") return `-${o.discount_value}%`;
  if (o.discount_type === "fixed") return `-€${Number(o.discount_value).toFixed(2)}`;
  if (o.discount_type === "free_delivery") return "Consegna gratis";
  return "Offerta";
}

export function orderWithCoupon(nav, o) {
  localStorage.setItem("pf_prefill_coupon", o.coupon_code);
  nav(`/locale/${o.tenant_slug}/${o.store_slug}`);
}

export function OfferCard({ o, onSaved, orderToken, saved }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [isSaved, setIsSaved] = useState(!!saved);
  const isCustomer = user && user.role === "customer";

  const track = (kind) => {
    api.post(`/portal/proximity/${o.campaign_id}/click`, { order_token: orderToken || null }).catch(() => {});
    if (kind === "order") orderWithCoupon(nav, o);
  };

  const save = async () => {
    if (!isCustomer) { toast.error("Accedi come cliente per salvare l'offerta"); nav("/account/login"); return; }
    try {
      await api.post("/account/coupons", { campaign_id: o.campaign_id });
      setIsSaved(true);
      toast.success("Coupon salvato nel portafoglio");
      onSaved && onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-dashed p-4 flex flex-col gap-3"
      style={{ borderColor: `${o.color}66`, background: `${o.color}0f` }}
      data-testid={`proximity-offer-${o.campaign_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 shrink-0 rounded-xl grid place-items-center overflow-hidden" style={{ background: `${o.color}22`, color: o.color }}>
          {o.logo ? <img src={o.logo} alt="" className="w-full h-full object-cover" /> : <Tag size={20} />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-sm truncate">{o.headline}</p>
          <p className="text-xs opacity-70 truncate">{o.store_name} · {o.brand}</p>
          <p className="text-[11px] opacity-60 flex items-center gap-1 mt-0.5"><MapPin size={11} /> {o.distance_km} km {o.address ? `· ${o.address}` : ""}</p>
        </div>
        <span className="shrink-0 text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: o.color, color: "#fff" }}>{discountLabel(o)}</span>
      </div>
      {o.description && <p className="text-xs opacity-75">{o.description}</p>}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-mono font-bold px-2 py-1 rounded" style={{ background: `${o.color}22`, color: o.color }}>{o.coupon_code}{o.min_order ? ` · min €${Number(o.min_order).toFixed(2)}` : " · senza minimo"}</span>
        <div className="flex items-center gap-2">
          <button onClick={save} disabled={isSaved} data-testid={`proximity-save-${o.campaign_id}`}
            className="h-9 px-3 rounded-full border text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60"
            style={{ borderColor: `${o.color}66`, color: o.color }}>
            {isSaved ? <><Check size={14} /> Salvato</> : <><BookmarkPlus size={14} /> Salva</>}
          </button>
          <button onClick={() => track("order")} data-testid={`proximity-order-${o.campaign_id}`}
            className="h-9 px-4 rounded-full text-xs font-semibold text-white inline-flex items-center gap-1 hover:opacity-90"
            style={{ background: o.color }}>
            Ordina ora <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function ProximityOffers({ orderToken, dark = false }) {
  const [offers, setOffers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!orderToken) return;
    api.get(`/portal/proximity?order_token=${orderToken}`)
      .then((r) => setOffers(r.data || []))
      .catch(() => setOffers([]))
      .finally(() => setLoaded(true));
  }, [orderToken]);

  if (!loaded || offers.length === 0) return null;

  return (
    <div className={`rounded-2xl border p-5 mt-4 ${dark ? "border-white/10 bg-white/[0.04]" : "bg-card"}`} data-testid="proximity-offers">
      <h3 className="font-semibold flex items-center gap-2 mb-1"><MapPin size={16} className="text-[#FFC400]" /> Offerte vicino a te</h3>
      <p className="text-xs text-muted-foreground mb-3 opacity-80">Sconti da altri locali Pickfood nelle vicinanze. Salvali o ordina subito.</p>
      <div className="space-y-3">
        {offers.map((o) => <OfferCard key={o.campaign_id} o={o} orderToken={orderToken} />)}
      </div>
    </div>
  );
}
